import { client } from '@/lib/api/client'
import {
  venueSettlementCyclesExportPath,
  venueSettlementCyclesPath,
  venueSettlementDeductionsPath,
  venueSettlementRevenuePath,
} from '@/lib/api/config'
import { toApiError } from '@/lib/api/errors'
import { downloadBlob } from '@/lib/utils/downloadFile'

import type {
  VenueSettlementCyclesView,
  VenueSettlementDeductionsView,
  VenueSettlementRevenueView,
} from './settlementTypes'
import {
  settlementCyclesCsvFileName,
  settlementCyclesQueryParams,
  settlementDeductionsQueryParams,
  settlementRevenueQueryParams,
  type SettlementCyclesFilter,
  type SettlementDeductionsFilter,
  type SettlementRevenueFilter,
} from './settlementViewModel'

/**
 * `GET .../settlements/revenue` (#703 §2) — 기간 축(매출 현황) + 회차 축(다음 정산) 요약.
 * 🚨 게이팅 대상이 아니다 — 항상 200. `preset` 해석은 서버가 하며, 응답의 `from`/`to`를 그대로 표시한다.
 */
export async function fetchVenueSettlementRevenue(
  venueId: string,
  filter: SettlementRevenueFilter
): Promise<VenueSettlementRevenueView> {
  try {
    const { data } = await client.get<VenueSettlementRevenueView>(venueSettlementRevenuePath(venueId), {
      params: settlementRevenueQueryParams(filter),
    })
    return data
  } catch (error) {
    throw toApiError(error)
  }
}

/**
 * `GET .../settlements/cycles` (#703 §3) — 커서 페이지네이션.
 * 🚨 커서는 다음 페이지의 첫 회차월(`YYYY-MM`) 그 자체다 — 서버가 준 값을 그대로 되싣는다.
 */
export async function fetchVenueSettlementCycles(
  venueId: string,
  filter: SettlementCyclesFilter,
  cursor?: string
): Promise<VenueSettlementCyclesView> {
  try {
    const { data } = await client.get<VenueSettlementCyclesView>(venueSettlementCyclesPath(venueId), {
      params: settlementCyclesQueryParams(filter, cursor),
    })
    return data
  } catch (error) {
    throw toApiError(error)
  }
}

/**
 * `GET .../settlements/deductions` (#703 §4) — 커서 페이지네이션.
 * 🚨 `deductionCount`/`deductionAmount`는 전체 집계다(페이지 건수가 아니다).
 */
export async function fetchVenueSettlementDeductions(
  venueId: string,
  filter: SettlementDeductionsFilter,
  cursor?: string
): Promise<VenueSettlementDeductionsView> {
  try {
    const { data } = await client.get<VenueSettlementDeductionsView>(
      venueSettlementDeductionsPath(venueId),
      { params: settlementDeductionsQueryParams(filter, cursor) }
    )
    return data
  } catch (error) {
    throw toApiError(error)
  }
}

/**
 * `GET .../settlements/cycles/export` (#703 §6) — 정산 회차 CSV 다운로드.
 * 🚨 `statuses` 필터가 없다 — 대사(對査)용 파일이라 그 기간의 전 회차를 담는다.
 * 🚨 파일명은 `Content-Disposition` 파싱에 기대지 않고 api-spec 문서화 패턴을 그대로 구성한다
 * (설계 §2 자율 결정 — 크로스오리진 배포에서 그 헤더가 노출되지 않을 위험을 감수하지 않는다).
 */
export async function downloadVenueSettlementCyclesCsv(
  venueId: string,
  filter: { fromMonth: string; toMonth: string }
): Promise<void> {
  try {
    const { data } = await client.get<Blob>(venueSettlementCyclesExportPath(venueId), {
      params: { fromMonth: filter.fromMonth, toMonth: filter.toMonth },
      responseType: 'blob',
    })
    downloadBlob(data, settlementCyclesCsvFileName(venueId, filter.fromMonth, filter.toMonth))
  } catch (error) {
    throw toApiError(error)
  }
}
